/**
 * Scheduled-content notification tap handler.
 *
 * Web port of Flutter's `PushNavigationService._showContentScheduledSheet`
 * (`lib/services/push_navigation_service.dart:419-450`), reached from the
 * `scheduleContent` case (`:210-219`) and completed by `_publishContent`
 * (`:498-513`). Two branches:
 *  - not yet published → a confirm sheet stating when the content is due, whose
 *    positive action force-publishes it *now* and toasts the outcome.
 *  - already published → a plain info sheet with a single Close button, and no
 *    publish call at all.
 *
 * NOT the `scheduleContentPublished` case — that is a different notification type
 * and routes to `contentNotificationTapHandler` (`:220-228`).
 *
 * Reused web surface (`conventions.md` → "Parity calibration — reuse beats
 * literal porting"). The same publish-a-scheduled-item flow already exists on web
 * at `ContentActionsContent.handlePublishScheduled` (`ContentActionsContent.tsx:164-188`)
 * over `useContentActions.publishScheduled`.
 * This handler shares that flow's owner module and l10n keys verbatim so web keeps ONE
 * scheduled-publish surface rather than two divergent ones. Two consequences of
 * that reuse, both deliberate:
 *  - The date is rendered with `formatDate(schedule, true)` — i.e. *with* time —
 *    whereas Flutter's `DateUtils.getDate(schedule)` (`:430`) omits it. This is a
 *    pre-existing web choice inherited from the content-actions surface, not a
 *    new divergence introduced here.
 *  - The failure toast reuses `contentActions.publishScheduledError`
 *    ('Could not publish the content. Please try again!'), which is already more
 *    specific than Flutter's shared literal 'There was an error. Please try
 *    again!' (`:508`, and the same literal at `:525` for the retry-payment flow).
 *    That one Flutter literal therefore maps to two web strings — this key here,
 *    and the retry-payment flow's own key — by design, rather than minting a
 *    second competing scheduled-publish error string.
 *
 * The publish write and its mandatory `CONTENT_ITEM_UPDATED` announcement are owned
 * by `presentation/feature/content/publishScheduledContent.ts`, shared with
 * `useContentActions.publishScheduled`.
 *
 * Pending caller: `notificationTap/notificationTapRouter.ts` (Task 13) is the
 * first and only consumer — this module is intentionally uncalled for exactly one
 * task, not dead code.
 */
import toast from 'react-hot-toast';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { publishScheduledContent } from '@/presentation/feature/content/publishScheduledContent';
import type { NotificationTapContext } from '@/presentation/feature/inbox/notificationTap/notificationTapContext';

/**
 * Handles a `scheduleContent` notification tap
 * (`push_navigation_service.dart:210-219`).
 *
 * Synchronous by design: Flutter does NOT await `_showContentScheduledSheet`
 * (`:212`), so the caller's mark-read (Task 14) fires immediately rather than
 * waiting for the user to answer the sheet. The sheet work therefore runs in a
 * fire-and-forget IIFE.
 */
export const handleScheduledContentNotificationTap = (context: NotificationTapContext): void => {
  const mediaInfo = context.notification.info.mediaInfo;
  // Flutter null-asserts both `mediaInfo!` and `mediaId!` (`:213-215`); every
  // `scheduleContent` document is written with them. Defensive no-op instead of
  // a crash on a malformed document — NOT a behaviour change.
  const mediaId = mediaInfo?.mediaId;
  if (!mediaInfo || !mediaId) return;

  // `published == null || !published` (`:425`) — a MISSING `published` takes the
  // publish-now branch, so the web equivalent is `!== true`, not `=== false`.
  const isPublished = mediaInfo.published === true;

  void (async () => {
    if (isPublished) {
      // `:441-448` — informational only; no publish call is made on this branch.
      await InfoSheet.show({
        title: context.l10n.contentActions.scheduledContentTitle,
        infoText: context.l10n.inbox.notificationTap.contentAlreadyPublished,
        okText: context.l10n.common.close,
        screenKey: context.screenKey,
        qaId: 'notification-content-already-published',
      });
      return;
    }

    // `schedule` is optional on `MediaInfoEntity` where Flutter null-asserts it
    // (`:215`); `?? 0` makes the formatter render its empty-date string instead
    // of crashing on a document missing the field.
    const formattedDate = context.formatDate(mediaInfo.schedule ?? 0, true);
    const description = context.l10n.contentActions.scheduledContentDescription.replace(
      '{date}',
      formattedDate,
    );

    // `succeeded`-flag + post-response toast shape copied from
    // `ContentActionsContent.tsx:169-187`. No `SheetService.close()` follow-up
    // here: unlike the content-actions caller there is no outer sheet to close.
    let publishSucceeded = false;
    const response = await ConfirmSheet.show({
      title: context.l10n.contentActions.scheduledContentTitle,
      confirmText: description,
      okText: context.l10n.contentActions.publishNow,
      cancelText: context.l10n.common.close,
      screenKey: context.screenKey,
      loadingStyle: 'button',
      qaId: 'notification-schedule-content-publish',
      onConfirm: async () => {
        // `_publishContent` (`:498-513`) → `MediasRepository().publishContent`,
        // plus the paired CONTENT_ITEM_UPDATED emit the shared owner performs.
        const success = await publishScheduledContent(mediaId);
        if (success) {
          publishSucceeded = true;
        } else {
          toast.error(context.l10n.contentActions.publishScheduledError);
        }
      },
    });

    if (response && publishSucceeded) {
      toast.success(context.l10n.contentActions.publishScheduledSuccess);
    }
  })();
};
